// UploadProductImage.tsx
import React, { useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type Props = {
  defaultCode?: string;
};

export default function UploadProductImage({ defaultCode = "" }: Props) {
  /** ---------- UI State ---------- */
  const [code, setCode] = useState(defaultCode);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // สถานะการอัปโหลดสำหรับ alert
  const [status, setStatus] = useState<{type: "success" | "warning" | "danger" | ""; msg: string}>({
    type: "",
    msg: "",
  });

  const canSubmit = useMemo(() => !!code && !!file && !submitting, [code, file, submitting]);

  /** ---------- Handlers ---------- */
  const onPick = (f: File | null) => {
    setFile(f);
    setStatus({ type: "", msg: "" });
    if (f) setPreview(URL.createObjectURL(f));
    else setPreview("");
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] ?? null;
    if (f) onPick(f);
  };

  // PMN-CRITICAL(/UploadProductImage): Field name must be "file"; /upload/:code overwrites the fixed product blob path and updates image columns.
  const upload = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setStatus({ type: "info" as any, msg: "กำลังอัปโหลด..." });

      const fd = new FormData();
      fd.append("file", file!); // field name = file

      const endpoint = apiUrl(`/upload/${encodeURIComponent(code)}`);
      const res = await fetch(endpoint, { method: "POST", body: fd });

      const ct = res.headers.get("content-type") || "";
      const payload: any = ct.includes("application/json") ? await res.json() : await res.text();

      if (!res.ok || (typeof payload === "object" && payload?.ok === false)) {
        const msg = (typeof payload === "object" ? payload?.error : String(payload)) || "upload_failed";
        throw new Error(msg);
      }

      const url = (typeof payload === "object" ? payload?.url : undefined) as string | undefined;
      setStatus({ type: "success", msg: `อัปโหลดสำเร็จ! ${url ? `URL: ${url}` : ""}` });
    } catch (err: any) {
      setStatus({ type: "danger", msg: `ล้มเหลว: ${err?.message || "error"}` });
    } finally {
      setSubmitting(false);
    }
  };

  /** ---------- UI ---------- */
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                {/* Header + action */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">อัปโหลดรูปสินค้า</h4>
                  <a href="/" className="btn btn-link text-decoration-none">← กลับหน้าเมนู</a>
                </div>

                {/* Status */}
                {status.msg && (
                  <div className={`alert alert-${status.type || "secondary"} rounded-3`} role="alert">
                    {status.msg}
                  </div>
                )}

                {/* ฟอร์ม */}
                <div className="border rounded-3 p-3 bg-white">
                  {/* product_code */}
                  <div className="mb-3">
                    <label htmlFor="code" className="form-label">รหัสสินค้า (product_code)</label>
                    <input
                      id="code"
                      className="form-control"
                      placeholder="เช่น dc025"
                      value={code}
                      onChange={(e) => setCode(e.target.value.trim())}
                    />
                  </div>

                  {/* dropzone + preview */}
                  <div className="mb-3">
                    <label className="form-label d-flex justify-content-between align-items-center">
                      ไฟล์รูปภาพ
                      {file && <span className="badge text-bg-secondary">{file.name}</span>}
                    </label>

                    <div
                      onDrop={onDrop}
                      onDragOver={(e) => e.preventDefault()}
                      className="w-100 d-flex align-items-center justify-content-center bg-light"
                      style={{
                        border: "2px dashed #c9ccd1",
                        borderRadius: 12,
                        padding: 20,
                        cursor: "pointer",
                        minHeight: 120,
                      }}
                      onClick={() => document.getElementById("file-input")?.click()}
                      title="ลากไฟล์มาวาง หรือคลิกเพื่อเลือกไฟล์"
                    >
                      <input
                        id="file-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
                        style={{ display: "none" }}
                      />
                      {preview ? (
                        <img
                          src={preview}
                          alt="preview"
                          className="img-fluid shadow-sm"
                          style={{ maxHeight: 220, borderRadius: 10, objectFit: "contain", background: "#fff" }}
                        />
                      ) : (
                        <div className="text-muted">ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์</div>
                      )}
                    </div>
                    <div className="form-text">รองรับไฟล์ภาพทั่วไป (jpeg, png, webp ฯลฯ)</div>
                  </div>

                  {/* ปุ่ม */}
                  <div className="d-grid">
                    <button
                      className="btn btn-primary btn-lg"
                      disabled={!canSubmit}
                      onClick={upload}
                    >
                      {submitting ? "กำลังอัปโหลด…" : "📤 อัปโหลด"}
                    </button>
                  </div>

                  {/* ปุ่มล้างค่า */}
                  <div className="d-flex gap-2 justify-content-between mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => { setFile(null); setPreview(""); setStatus({ type: "", msg: "" }); }}
                    >
                      ล้างไฟล์
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => { setCode(""); setStatus({ type: "", msg: "" }); }}
                    >
                      ล้างรหัส
                    </button>
                  </div>
                </div>

                <div className="text-center small text-muted mt-3">
                  * ใช้การ์ด/โทนเดียวกับหน้า Stock In เพื่อความสอดคล้องของ UI
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>  
    </div>
  );
}
